import { useEffect, useMemo, useRef, useState } from "react";
import { config } from "../config";
import { accuracyLabel, useLocation } from "../broadcast/useLocation";
import { uploadEmergencyReport } from "./uploadEmergencyReport";
import { prepareEmergencyImage } from "./prepareEmergencyImage";
import { detectSupportLanguage, initialReply, initSupportCase, noteSupportReportSent, reportSupportCase, sendSupportMessage, setSupportIdentity, type EmergencyLanguage } from "./supportAssistant";

type ChatTurn = { role: "assistant" | "user"; text: string };
type DeviceSnapshot = Record<string, string | number | boolean | null>;
type Copy = {
  eyebrow: string;
  title: string;
  lede: string;
  statusReady: (label: string) => string;
  statusDenied: string;
  statusLoading: string;
  sent: string;
  takePhoto: string;
  takePhotoFallback: string;
  sending: string;
  retryLocation: string;
  locationGateTitle: string;
  locationGateBody: string;
  locationDeniedHelp: string;
  fallbackTitle: string;
  fallbackBody: string;
  landmarkLabel: string;
  landmarkPlaceholder: string;
  cameraNote: string;
  supportTitle: string;
  supportSub: string;
  calmCue: string;
  inputPlaceholder: string;
  send: string;
  supportInit: string;
  successMessage: string;
  processingPhoto: string;
  failedSend: string;
  shareDetailsTitle: string;
  shareDetailsSub: string;
  detailName: string;
  detailAge: string;
  detailPeople: string;
  detailInjury: string;
  detailsSend: string;
  detailsThanks: string;
  languageEn: string;
  languageEs: string;
  languageNl: string;
};

const COPY: Record<EmergencyLanguage, Copy> = {
  en: {
    eyebrow: "Emergency localizer",
    title: "Report a person in danger",
    lede: "Use this system when you need to ask for help fast, or when you find someone who needs urgent help.",
    statusReady: (label) => `Location ready · ${label}`,
    statusDenied: "Location unavailable",
    statusLoading: "Getting location…",
    sent: "Sent successfully",
    takePhoto: "Take photo and send",
    takePhotoFallback: "Take photo and send without GPS",
    sending: "Sending…",
    retryLocation: "Retry location",
    locationGateTitle: "Location is required",
    locationGateBody: "We need your device location before you can send a photo. Tap retry and allow location so the report can go out.",
    locationDeniedHelp: "If location was denied, open browser settings for this site and allow location, then tap retry.",
    fallbackTitle: "GPS is off, but we can still help",
    fallbackBody: "Add a landmark, street, building, or any clue that helps responders. We will send the photo, device details, and your source IP as a coarse trace.",
    landmarkLabel: "Landmark or clue",
    landmarkPlaceholder: "Example: near the blue wall, behind the supermarket, next to the church",
    cameraNote: "If the camera fails, use the image picker instead. Location is required to complete the report.",
    supportTitle: "I’m with you",
    supportSub: "Just breathe. You do not need to choose anything else right now.",
    calmCue: "Slow breathing. Pick only one small step: type or wait.",
    inputPlaceholder: "Type a short message",
    send: "Send",
    supportInit: "I’m here with you. We’ll keep this very small: breathe slowly, stay as still as you safely can, and I’ll stay with you.",
    successMessage: "Your image and location have been sent successfully to the authorities.",
    processingPhoto: "Processing photo…",
    failedSend: "Could not send the emergency report",
    shareDetailsTitle: "First name and details",
    shareDetailsSub: "Please share at least a first name so responders can record this submission. Add only what feels safe.",
    detailName: "First name *",
    detailAge: "Age or age range",
    detailPeople: "People with you",
    detailInjury: "Injury or medical need",
    detailsSend: "Share details",
    detailsThanks: "Thanks. I’m passing that along now.",
    languageEn: "🇺🇸 English",
    languageEs: "🇻🇪 Spanish",
    languageNl: "🇳🇱 Dutch",
  },
  es: {
    eyebrow: "Localizador de emergencia",
    title: "Reporta a una persona en peligro",
    lede: "Usa este sistema cuando necesites pedir ayuda rápido, o cuando encuentres a alguien que necesite ayuda urgente.",
    statusReady: (label) => `Ubicación lista · ${label}`,
    statusDenied: "Ubicación no disponible",
    statusLoading: "Obteniendo ubicación…",
    sent: "Enviado con éxito",
    takePhoto: "Tomar foto y enviar",
    takePhotoFallback: "Tomar foto y enviar sin GPS",
    sending: "Enviando…",
    retryLocation: "Reintentar ubicación",
    locationGateTitle: "La ubicación es obligatoria",
    locationGateBody: "Necesitamos la ubicación de tu dispositivo antes de enviar la foto. Pulsa reintentar y permite la ubicación para poder enviar el reporte.",
    locationDeniedHelp: "Si negaste la ubicación, abre la configuración del navegador para este sitio, permite la ubicación y vuelve a intentarlo.",
    fallbackTitle: "El GPS está apagado, pero aún podemos ayudar",
    fallbackBody: "Agrega una referencia, calle, edificio o cualquier pista para ayudar a los rescatistas. Enviaremos la foto, los datos del dispositivo y tu IP como rastro aproximado.",
    landmarkLabel: "Referencia o pista",
    landmarkPlaceholder: "Ejemplo: cerca del muro azul, detrás del supermercado, junto a la iglesia",
    cameraNote: "Si la cámara falla, usa el selector de imágenes. La ubicación es necesaria para completar el reporte.",
    supportTitle: "Estoy contigo",
    supportSub: "Solo respira. No necesitas elegir nada más ahora.",
    calmCue: "Respira lento. Elige solo una cosa pequeña: escribir o esperar.",
    inputPlaceholder: "Escribe un mensaje corto",
    send: "Enviar",
    supportInit: "Estoy contigo. Vamos a mantener esto muy pequeño: respira despacio, mantente lo más quieto posible y me quedo contigo.",
    successMessage: "Tu imagen y ubicación se han enviado correctamente a las autoridades.",
    processingPhoto: "Procesando foto…",
    failedSend: "No se pudo enviar el reporte de emergencia",
    shareDetailsTitle: "Nombre y detalles",
    shareDetailsSub: "Comparte al menos un nombre para registrar este envío. Agrega solo lo que te resulte seguro.",
    detailName: "Nombre *",
    detailAge: "Edad o rango",
    detailPeople: "Personas contigo",
    detailInjury: "Lesión o necesidad médica",
    detailsSend: "Compartir detalles",
    detailsThanks: "Gracias. Lo estoy enviando ahora.",
    languageEn: "🇺🇸 Inglés",
    languageEs: "🇻🇪 Español",
    languageNl: "🇳🇱 Neerlandés",
  },
  nl: {
    eyebrow: "Noodlocator",
    title: "Meld iemand in gevaar",
    lede: "Gebruik dit systeem wanneer je snel hulp nodig hebt, of wanneer je iemand vindt die dringend hulp nodig heeft.",
    statusReady: (label) => `Locatie klaar · ${label}`,
    statusDenied: "Locatie niet beschikbaar",
    statusLoading: "Locatie ophalen…",
    sent: "Succesvol verzonden",
    takePhoto: "Foto maken en verzenden",
    takePhotoFallback: "Foto maken en verzenden zonder GPS",
    sending: "Verzenden…",
    retryLocation: "Locatie opnieuw proberen",
    locationGateTitle: "Locatie is vereist",
    locationGateBody: "We hebben de locatie van je apparaat nodig voordat je een foto kunt verzenden. Tik op opnieuw proberen en sta locatie toe.",
    locationDeniedHelp: "Als locatie is geweigerd, open dan de browserinstellingen voor deze site, sta locatie toe en probeer opnieuw.",
    fallbackTitle: "GPS staat uit, maar we kunnen nog steeds helpen",
    fallbackBody: "Voeg een herkenningspunt, straat, gebouw of andere aanwijzing toe. We sturen de foto, apparaatgegevens en je bron-IP als globale trace.",
    landmarkLabel: "Herkenningspunt of aanwijzing",
    landmarkPlaceholder: "Bijvoorbeeld: bij de blauwe muur, achter de supermarkt, naast de kerk",
    cameraNote: "Als de camera niet werkt, gebruik dan de fotokiezer. Locatie is nodig om het rapport te verzenden.",
    supportTitle: "Ik ben bij je",
    supportSub: "Adem rustig. Je hoeft nu niets te kiezen.",
    calmCue: "Rustig ademen. Kies maar één kleine stap: typen of wachten.",
    inputPlaceholder: "Typ een kort bericht",
    send: "Versturen",
    supportInit: "Ik ben hier bij je. Laten we dit heel klein houden: adem langzaam, blijf zo stil mogelijk en ik blijf bij je.",
    successMessage: "Je foto en locatie zijn succesvol naar de autoriteiten gestuurd.",
    processingPhoto: "Foto verwerken…",
    failedSend: "Noodrapport kon niet worden verzonden",
    shareDetailsTitle: "Voornaam en details",
    shareDetailsSub: "Deel ten minste een voornaam zodat deze melding kan worden vastgelegd. Voeg alleen toe wat veilig voelt.",
    detailName: "Voornaam *",
    detailAge: "Leeftijd of leeftijdsrange",
    detailPeople: "Mensen bij je",
    detailInjury: "Letsel of medische nood",
    detailsSend: "Details delen",
    detailsThanks: "Dank je. Ik stuur dit nu door.",
    languageEn: "🇺🇸 Engels",
    languageEs: "🇻🇪 Spaans",
    languageNl: "🇳🇱 Nederlands",
  },
};

const CASE_STORAGE_KEY = "amigoe-live-emergency-case-id";

function guessBrowserLanguage(): EmergencyLanguage {
  const langs = typeof navigator !== "undefined" ? navigator.languages ?? [navigator.language] : [];
  const normalized = langs.map((value) => String(value).toLowerCase());
  if (normalized.some((value) => value.startsWith("en"))) return "en";
  if (normalized.some((value) => value.startsWith("nl"))) return "nl";
  if (normalized.some((value) => value.startsWith("es"))) return "es";
  return "en";
}

function getOrCreateCaseId() {
  try {
    const existing = sessionStorage.getItem(CASE_STORAGE_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(CASE_STORAGE_KEY, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

function collectDeviceSnapshot(): DeviceSnapshot {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const scr = typeof screen !== "undefined" ? screen : undefined;
  const deviceNav = nav as Navigator & {
    deviceMemory?: number;
    connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
  } | undefined;
  const conn = deviceNav?.connection;
  return {
    userAgent: nav?.userAgent ?? "",
    platform: nav?.platform ?? "",
    vendor: nav?.vendor ?? "",
    language: nav?.language ?? "",
    languages: nav?.languages?.slice(0, 3).join(",") ?? "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
    online: typeof navigator !== "undefined" ? navigator.onLine : false,
    cookieEnabled: nav?.cookieEnabled ?? false,
    hardwareConcurrency: nav?.hardwareConcurrency ?? null,
    deviceMemory: typeof deviceNav?.deviceMemory === "number" ? deviceNav.deviceMemory : null,
    screen: scr ? `${scr.width}x${scr.height}` : "",
    viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
    pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    touchPoints: nav?.maxTouchPoints ?? 0,
    connectionType: conn?.effectiveType ?? "",
    connectionDownlink: conn?.downlink ?? null,
    connectionRtt: conn?.rtt ?? null,
    connectionSaveData: conn?.saveData ?? false,
  };
}

export default function EmergencyPage() {
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const caseIdRef = useRef<string>(getOrCreateCaseId());
  const browserDetectDoneRef = useRef(false);
  const locationDetectDoneRef = useRef(false);
  const [language, setLanguage] = useState<EmergencyLanguage>(() => guessBrowserLanguage());
  const [languageMode, setLanguageMode] = useState<"auto" | "manual">("auto");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string>(COPY.en.statusLoading);
  const [error, setError] = useState<string | null>(null);
  const [sentReportId, setSentReportId] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState("");
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [detailName, setDetailName] = useState("");
  const [detailAge, setDetailAge] = useState("");
  const [detailPeople, setDetailPeople] = useState("");
  const [detailInjury, setDetailInjury] = useState("");
  const [landmark, setLandmark] = useState("");
  const copy = COPY[language];
  const [chatTurns, setChatTurns] = useState<ChatTurn[]>([
    {
      role: "assistant",
      text: copy.supportInit,
    },
  ]);
  const ready = Boolean(location.state.current);

  useEffect(() => {
    location.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state.current) return;
    if (location.state.status === "denied") return;

    const timer = window.setInterval(() => {
      if (!location.state.current && location.state.status !== "denied") {
        location.start();
      }
    }, 3500);

    return () => window.clearInterval(timer);
  }, [location.state.current, location.state.status, location.start]);

  useEffect(() => {
    if (languageMode !== "auto") return;
    if (browserDetectDoneRef.current) return;

    let cancelled = false;
    const browserLanguages = typeof navigator !== "undefined" ? navigator.languages ?? [navigator.language] : [];

    void detectSupportLanguage({
      caseId: caseIdRef.current,
      browserLanguages,
    }).then((detected) => {
      browserDetectDoneRef.current = true;
      if (cancelled || detected === language) return;
      setLanguage(detected);
    }).finally(() => {
      if (!cancelled) browserDetectDoneRef.current = true;
    });

    return () => {
      cancelled = true;
    };
  }, [languageMode, language]);

  useEffect(() => {
    if (languageMode !== "auto") return;
    if (locationDetectDoneRef.current) return;
    if (!location.state.current) return;

    let cancelled = false;
    const browserLanguages = typeof navigator !== "undefined" ? navigator.languages ?? [navigator.language] : [];
    const currentLocation = { lat: location.state.current.lat, lon: location.state.current.lon };

    void detectSupportLanguage({
      caseId: caseIdRef.current,
      browserLanguages,
      location: currentLocation,
    }).then((detected) => {
      locationDetectDoneRef.current = true;
      if (cancelled || detected === language) return;
      setLanguage(detected);
    }).finally(() => {
      if (!cancelled) locationDetectDoneRef.current = true;
    });

    return () => {
      cancelled = true;
    };
  }, [languageMode, location.state.current, language]);

  useEffect(() => {
    if (!sentReportId) return;

    let cancelled = false;
    setChatTurns([{ role: "assistant", text: initialReply(language) }]);
    void initSupportCase(caseIdRef.current, language).then((assistantText) => {
      if (cancelled) return;
      setChatTurns([{ role: "assistant", text: assistantText }]);
    });

    return () => {
      cancelled = true;
    };
  }, [language, sentReportId]);

  useEffect(() => {
    if (location.state.current) {
      const acc = accuracyLabel(location.state.current.accuracyM);
      setMessage(copy.statusReady(acc.label));
      return;
    }
    if (location.state.status === "denied" || location.state.status === "timeout" || location.state.status === "unavailable") {
      setMessage(copy.statusDenied);
      return;
    }
    setMessage(copy.statusLoading);
  }, [location.state, copy]);

  const readyText = useMemo(() => {
    if (location.state.current) {
      const p = location.state.current;
      return `${p.lat.toFixed(6)}, ${p.lon.toFixed(6)}`;
    }
    return "";
  }, [location.state.current]);

  const openCapture = () => {
    if (sending) return;
    inputRef.current?.click();
  };

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !location.state.current) return;

    setSending(true);
    setError(null);
    try {
      setMessage(copy.processingPhoto);
      const uploadBlob = await prepareEmergencyImage(file);
      const result = await uploadEmergencyReport({
        gatewayUrl: config.gatewayUrl,
        caseId: caseIdRef.current,
        photo: uploadBlob,
        location: location.state.current,
        device: collectDeviceSnapshot(),
        landmark: landmark.trim(),
        note: "emergency-photo",
      });
      setSentReportId(result.reportId);
      setMessage(copy.sent);
      setIntakeOpen(true);
      const assistantText = await noteSupportReportSent(caseIdRef.current, result.reportId, language);
      setChatTurns((prev) => [...prev, { role: "assistant", text: assistantText }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : copy.failedSend);
    } finally {
      setSending(false);
    }
  };

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatInput("");
    setChatTurns((prev) => [...prev, { role: "user", text }]);
    void sendSupportMessage(caseIdRef.current, text, language).then((assistantText) => {
      setChatTurns((prev) => [...prev, { role: "assistant", text: assistantText }]);
    });
  };

  const postEmergencyDetails = async () => {
    const firstName = detailName.trim();
    if (!sentReportId || !firstName) return;

    const res = await fetch(`${config.gatewayUrl.replace(/\/$/, "")}/emergency/report/${encodeURIComponent(sentReportId)}/details`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        caseId: caseIdRef.current,
        firstName,
        age: detailAge.trim() || null,
        people: detailPeople.trim() || null,
        injury: detailInjury.trim() || null,
      }),
    });

    if (!res.ok) {
      throw new Error(`Could not save details (${res.status})`);
    }
  };

  const sendDetails = () => {
    if (!sentReportId) return;
    const firstName = detailName.trim();
    if (!firstName) return;
    const parts = [
      `${copy.detailName.replace(" *", "")}: ${firstName}`,
      detailAge.trim() ? `${copy.detailAge}: ${detailAge.trim()}` : "",
      detailPeople.trim() ? `${copy.detailPeople}: ${detailPeople.trim()}` : "",
      detailInjury.trim() ? `${copy.detailInjury}: ${detailInjury.trim()}` : "",
    ].filter(Boolean);
    if (parts.length === 0) return;

    setIntakeOpen(false);
    setChatTurns((prev) => [...prev, { role: "user", text: parts.join(" · ") }]);
    setMessage(copy.detailsThanks);
    void postEmergencyDetails()
      .then(() => Promise.all([
        setSupportIdentity(caseIdRef.current, firstName, language),
        sendSupportMessage(
          caseIdRef.current,
          `Additional crisis details after report ${sentReportId}: ${parts.join(" · ")}`,
          language
        ),
        reportSupportCase(caseIdRef.current, language),
      ]))
      .then(([identityText, assistantText, reportText]) => {
        if (identityText) {
          setChatTurns((prev) => [...prev, { role: "assistant", text: identityText }]);
        }
        if (assistantText) {
          setChatTurns((prev) => [...prev, { role: "assistant", text: assistantText }]);
        }
        if (reportText) {
          setChatTurns((prev) => [...prev, { role: "assistant", text: reportText }]);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : copy.failedSend);
      });
  };

  return (
    <div className="emergency">
      <section className="emergency__panel">
        <div className="emergency__language" role="tablist" aria-label={language === "nl" ? "Noodtaal" : language === "es" ? "Idioma de emergencia" : "Emergency language"}>
          <button
            type="button"
            className={language === "en" ? "primary" : "ghost"}
            aria-pressed={language === "en"}
            onClick={() => {
              setLanguageMode("manual");
              setChatTurns([{ role: "assistant", text: initialReply("en") }]);
              setLanguage("en");
            }}
          >
            {copy.languageEn}
          </button>
          <button
            type="button"
            className={language === "es" ? "primary" : "ghost"}
            aria-pressed={language === "es"}
            onClick={() => {
              setLanguageMode("manual");
              setChatTurns([{ role: "assistant", text: initialReply("es") }]);
              setLanguage("es");
            }}
          >
            {copy.languageEs}
          </button>
          <button
            type="button"
            className={language === "nl" ? "primary" : "ghost"}
            aria-pressed={language === "nl"}
            onClick={() => {
              setLanguageMode("manual");
              setChatTurns([{ role: "assistant", text: initialReply("nl") }]);
              setLanguage("nl");
            }}
          >
            {copy.languageNl}
          </button>
        </div>
        <div className="emergency__eyebrow">{copy.eyebrow}</div>
        <h1>{copy.title}</h1>
        <p className="muted">{copy.lede}</p>

        <div className={`emergency__status ${ready ? "emergency__status--ready" : ""}`}>
          {message}
          {sentReportId && <span>{copy.successMessage}</span>}
          {readyText && <span>{readyText}</span>}
        </div>

        {error && <p className="err">{error}</p>}

        {!ready ? (
          <section className="emergency__location-gate" aria-label="Location required">
            <strong>{copy.locationGateTitle}</strong>
            <p className="muted">{copy.locationGateBody}</p>
            <p className="muted small">{copy.locationDeniedHelp}</p>
            <div className="row emergency__actions">
              <button type="button" className="primary danger emergency__cta" onClick={location.start} disabled={sending}>
                {copy.retryLocation}
              </button>
            </div>
            <div className="emergency__fallback">
              <strong>{copy.fallbackTitle}</strong>
              <p className="muted">{copy.fallbackBody}</p>
              <label>
                <span className="emergency__label">{copy.landmarkLabel}</span>
                <input
                  type="text"
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  placeholder={copy.landmarkPlaceholder}
                  disabled={sending}
                />
              </label>
              <div className="row emergency__actions">
                <button type="button" className="primary danger emergency__cta" onClick={openCapture} disabled={sending}>
                  {sending ? copy.sending : copy.takePhotoFallback}
                </button>
              </div>
            </div>
          </section>
        ) : (
          <div className="row emergency__actions">
            <button type="button" className="primary danger emergency__cta" onClick={openCapture} disabled={sending}>
              {sending ? copy.sending : copy.takePhoto}
            </button>
            <button type="button" className="ghost" onClick={location.start} disabled={sending}>
              {copy.retryLocation}
            </button>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="user"
          className="emergency__file"
          onChange={onFile}
        />

        <p className="muted small emergency__note">{copy.cameraNote}</p>

        {ready && sentReportId && intakeOpen && (
          <section className="emergency__support" aria-label="Optional details">
            <div className="emergency__support-head">
              <strong>{copy.shareDetailsTitle}</strong>
              <span className="muted small">{copy.shareDetailsSub}</span>
            </div>
            <div className="emergency__chatbar" style={{ flexWrap: "wrap" }}>
              <input type="text" value={detailName} onChange={(e) => setDetailName(e.target.value)} placeholder={copy.detailName} aria-label={copy.detailName} />
              <input type="text" value={detailAge} onChange={(e) => setDetailAge(e.target.value)} placeholder={copy.detailAge} aria-label={copy.detailAge} />
              <input type="text" value={detailPeople} onChange={(e) => setDetailPeople(e.target.value)} placeholder={copy.detailPeople} aria-label={copy.detailPeople} />
              <input type="text" value={detailInjury} onChange={(e) => setDetailInjury(e.target.value)} placeholder={copy.detailInjury} aria-label={copy.detailInjury} />
            </div>
            <div className="row emergency__actions">
              <button type="button" className="primary" onClick={sendDetails} disabled={!detailName.trim()}>
                {copy.detailsSend}
              </button>
            </div>
          </section>
        )}

        {ready && sentReportId && (
          <section className="emergency__support emergency__support--compact" aria-label="Calm support chat">
            <div className="emergency__support-head">
              <strong>{copy.supportTitle}</strong>
              <span className="muted small">{copy.supportSub}</span>
            </div>
            <div className="emergency__calm-cue muted small">{copy.calmCue}</div>
            <div className="emergency__chat" role="log" aria-live="polite">
              {chatTurns.map((turn, index) => (
                <div key={`${turn.role}-${index}`} className={`emergency__bubble emergency__bubble--${turn.role}`}>
                  {turn.text}
                </div>
              ))}
            </div>
            <div className="emergency__chatbar emergency__chatbar--compact">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={copy.inputPlaceholder}
                aria-label="Support chat message"
              />
              <button type="button" className="primary" onClick={sendChat} disabled={!chatInput.trim()}>
                {copy.send}
              </button>
            </div>
          </section>
        )}
      </section>
    </div>
  );
}
